import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, LogOut, ChevronDown, Building } from 'lucide-react';

interface UserProfileProps {
  onNavigateToConfig: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onNavigateToConfig }) => {
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (!user) return null;

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserName = () => {
    return user.name || user.username || 'Usuario';
  };

  const getUserEmail = () => {
    return user.username || user.homeAccountId?.split('.')[0] || '';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-3 p-2.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all group border border-neutral-200 dark:border-neutral-700"
      >
        {/* Avatar Profesional */}
        <div className="relative">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
            <span className="text-white text-sm font-bold">
              {getInitials(getUserName())}
            </span>
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-success rounded-full border-2 border-white dark:border-neutral-900"></div>
        </div>

        {/* User Info Profesional */}
        <div className="text-left hidden md:block">
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 transition-colors">
            {getUserName()}
          </p>
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {getUserEmail()}
          </p>
        </div>

        <ChevronDown
          size={16}
          className={`text-neutral-600 dark:text-neutral-400 transition-all duration-300 ${
            isDropdownOpen ? 'rotate-180 text-primary' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu Profesional */}
      {isDropdownOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsDropdownOpen(false)}
          />
          
          {/* Menu Profesional */}
          <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 z-20 overflow-hidden">
            <div className="p-5 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-lg">
                      {getInitials(getUserName())}
                    </span>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-white dark:border-neutral-900"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-neutral-900 dark:text-neutral-50 text-base mb-1 truncate">
                    {getUserName()}
                  </p>
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400 truncate">
                    {getUserEmail()}
                  </p>
                  <div className="mt-1.5 flex items-center space-x-1.5">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-xs font-medium text-success">En línea</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-2">
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  // Aquí podrías agregar navegación al perfil
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all text-left group"
              >
                <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center group-hover:bg-primary/20 dark:group-hover:bg-primary/30 transition-colors">
                  <User size={18} className="text-primary" />
                </div>
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">Mi Perfil</span>
              </button>

              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  onNavigateToConfig();
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all text-left group"
              >
                <div className="w-9 h-9 bg-neutral-200 dark:bg-neutral-700 rounded-lg flex items-center justify-center group-hover:bg-neutral-300 dark:group-hover:bg-neutral-600 transition-colors">
                  <Building size={18} className="text-neutral-600 dark:text-neutral-400" />
                </div>
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">Configuración</span>
              </button>

              <div className="border-t border-neutral-200 dark:border-neutral-700 my-2"></div>

              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  logout();
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-error/10 dark:hover:bg-error/20 transition-all text-left group"
              >
                <div className="w-9 h-9 bg-error/10 dark:bg-error/20 rounded-lg flex items-center justify-center group-hover:bg-error/20 dark:group-hover:bg-error/30 transition-colors">
                  <LogOut size={18} className="text-error" />
                </div>
                <span className="text-sm font-medium text-error">Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfile;